// src/pages/user/Messages.js
// Page messages avec contact assistance (WhatsApp) - NE PAS MODIFIER

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import { APP_CONFIG } from '../../constants/config';

const Messages = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [message, setMessage] = useState('');

  const cleanPhone = APP_CONFIG.SUPPORT_WHATSAPP.replace(/\D/g, '');

  const handleWhatsAppDirect = () => {
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const handleSendMessage = () => {
    if (!message.trim()) return;
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/${cleanPhone}?text=${encoded}`, '_blank');
    setMessage('');
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.bg },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px',
      position: 'sticky',
      top: 0,
      backgroundColor: theme.bg,
      zIndex: 50,
      borderBottom: `1px solid ${theme.borderSoft}`,
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: theme.textPrimary,
      fontSize: '20px',
      cursor: 'pointer',
      padding: '4px',
    },
    title: { fontSize: '20px', fontWeight: '700', color: theme.textPrimary },
    assistanceCard: {
      margin: '16px',
      padding: '20px',
      backgroundColor: theme.bgCard,
      borderRadius: '16px',
      border: `1px solid ${theme.border}`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '10px',
      textAlign: 'center',
    },
    assistanceIcon: {
      width: '56px',
      height: '56px',
      borderRadius: '50%',
      backgroundColor: 'rgba(37, 211, 102, 0.12)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '26px',
    },
    assistanceTitle: { fontSize: '16px', fontWeight: '700', color: theme.textPrimary },
    assistanceText: { fontSize: '13px', color: theme.textSecondary, lineHeight: '1.5', maxWidth: '280px' },
    whatsappBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%',
      padding: '14px',
      backgroundColor: '#25D366',
      border: 'none',
      borderRadius: '10px',
      color: '#FFFFFF',
      fontSize: '15px',
      fontWeight: '700',
      cursor: 'pointer',
      marginTop: '6px',
    },
    divider: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      margin: '4px 16px',
      color: theme.textMuted,
      fontSize: '12px',
    },
    dividerLine: { flex: 1, height: '1px', backgroundColor: theme.borderSoft },
    formSection: {
      padding: '0 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    },
    formLabel: { fontSize: '13px', fontWeight: '600', color: theme.textSecondary },
    textarea: {
      padding: '14px 16px',
      backgroundColor: theme.bgInput,
      border: `1px solid ${theme.border}`,
      borderRadius: '10px',
      color: theme.textPrimary,
      fontSize: '14px',
      outline: 'none',
      minHeight: '110px',
      resize: 'vertical',
      fontFamily: 'inherit',
    },
    sendBtn: {
      padding: '14px',
      backgroundColor: theme.gold,
      border: 'none',
      borderRadius: '10px',
      color: theme.mode === 'light' ? '#FFFFFF' : '#0A0E1A',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
    },
    sendBtnDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    emptyContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 24px 24px',
      textAlign: 'center',
      gap: '10px',
    },
    emptyIcon: { fontSize: '40px', opacity: 0.4 },
    emptyTitle: { fontSize: '14px', fontWeight: '600', color: theme.textPrimary },
    emptyText: { fontSize: '13px', color: theme.textSecondary, maxWidth: '280px', lineHeight: '1.5' },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Messages | Fynho</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <div style={styles.title}>💬 {t('nav.messages')}</div>
      </div>

      <div style={styles.assistanceCard}>
        <div style={styles.assistanceIcon}>💬</div>
        <div style={styles.assistanceTitle}>Besoin d'aide ?</div>
        <div style={styles.assistanceText}>
          Contactez notre équipe directement sur WhatsApp, on vous répond rapidement.
        </div>
        <button style={styles.whatsappBtn} onClick={handleWhatsAppDirect}>
          💬 Ouvrir WhatsApp
        </button>
      </div>

      <div style={styles.divider}>
        <div style={styles.dividerLine} />
        <span>ou envoyer un message</span>
        <div style={styles.dividerLine} />
      </div>

      <div style={styles.formSection}>
        <label style={styles.formLabel}>Votre message</label>
        <textarea
          style={styles.textarea}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Décrivez votre question ou votre besoin..."
        />
        <button
          style={{ ...styles.sendBtn, ...(!message.trim() ? styles.sendBtnDisabled : {}) }}
          onClick={handleSendMessage}
          disabled={!message.trim()}
        >
          Envoyer via WhatsApp
        </button>
      </div>

      <div style={styles.emptyContainer}>
        <div style={styles.emptyIcon}>📭</div>
        <div style={styles.emptyTitle}>Aucun message dans l'historique</div>
        <div style={styles.emptyText}>
          Les échanges concernant vos annonces se font directement avec les propriétaires via WhatsApp.
        </div>
      </div>
    </div>
  );
};

export default Messages;